import React from 'react'
import { Button, Card, Form, Image, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Typography, message } from 'antd'
import { listShops, createShop, updateShop, deleteShop } from '../../api/shops'
import ImageUploadField from '../../components/ImageUploadField'
import { ADMIN_SHOP_IMAGE_PLACEHOLDER, resolveAdminImageUrl } from '../../utils/adminImageUpload'

export default function ShopsAdmin() {
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [createOpen, setCreateOpen] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const [editOpen, setEditOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [selected, setSelected] = React.useState(null)

  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const createImageRef = React.useRef(null)
  const editImageRef = React.useRef(null)

  const fetchData = React.useCallback(async () => {
    setLoading(true)
    try {
      const items = await listShops()
      setData(items || [])
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { fetchData() }, [fetchData])

  function parseError(e) {
    const t = (e && e.message) ? e.message : 'Request failed'
    return t.replace(/^\s*"?|"?\s*$/g, '')
  }

  function toHHmm(value) {
    if (!value) return undefined
    const s = String(value)
    if (s.length >= 5) return s.slice(0, 5)
    return undefined
  }

  async function onCreate(values) {
    setCreating(true)
    try {
      const imageUrl = await resolveAdminImageUrl(values, createImageRef)
      const payload = {
        nameAr: values.nameAr?.trim() || undefined,
        phoneNumber: values.phoneNumber?.trim() || undefined,
        imageUrl,
        openTime: values.openTime?.trim() || undefined,
        closeTime: values.closeTime?.trim() || undefined,
        openTimeWeekend: values.openTimeWeekend?.trim() || undefined,
        closeTimeWeekend: values.closeTimeWeekend?.trim() || undefined,
        hidden: !!values.hidden,
        revenueModel: values.revenueModel || 'MARGIN_FROM_SELL',
        revenuePercentage: values.revenuePercentage != null && values.revenuePercentage !== '' ? values.revenuePercentage : undefined,
      }
      await createShop(payload)
      message.success('تم الإنشاء')
      setCreateOpen(false)
      createImageRef.current?.clearPendingFile?.()
      createForm.resetFields()
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setCreating(false)
    }
  }

  function openEdit(record) {
    editImageRef.current?.clearPendingFile?.()
    setSelected(record)
    editForm.setFieldsValue({
      nameAr: record.nameAr,
      phoneNumber: record.phoneNumber,
      imageUrl: record.imageUrl,
      openTime: toHHmm(record.openTime),
      closeTime: toHHmm(record.closeTime),
      openTimeWeekend: toHHmm(record.openTimeWeekend),
      closeTimeWeekend: toHHmm(record.closeTimeWeekend),
      hidden: !!record.hidden,
      revenueModel: record.revenueModel || 'MARGIN_FROM_SELL',
      revenuePercentage: record.revenuePercentage != null ? Number(record.revenuePercentage) : undefined,
    })
    setEditOpen(true)
  }

  async function onEdit(values) {
    if (!selected) return
    setEditing(true)
    try {
      const payload = {}
      if (values.nameAr !== selected.nameAr) payload.nameAr = values.nameAr?.trim()
      if (values.phoneNumber !== selected.phoneNumber) payload.phoneNumber = values.phoneNumber?.trim()
      const imageUrl = await resolveAdminImageUrl(values, editImageRef)
      if ((imageUrl || '') !== (selected.imageUrl || '')) payload.imageUrl = imageUrl || undefined

      const selOpen = toHHmm(selected.openTime)
      const selClose = toHHmm(selected.closeTime)
      const selOpenW = toHHmm(selected.openTimeWeekend)
      const selCloseW = toHHmm(selected.closeTimeWeekend)
      if (values.openTime !== selOpen) payload.openTime = values.openTime?.trim()
      if (values.closeTime !== selClose) payload.closeTime = values.closeTime?.trim()
      if (values.openTimeWeekend !== selOpenW) payload.openTimeWeekend = values.openTimeWeekend?.trim()
      if (values.closeTimeWeekend !== selCloseW) payload.closeTimeWeekend = values.closeTimeWeekend?.trim()
      if (!!values.hidden !== !!selected.hidden) payload.hidden = !!values.hidden
      if ((values.revenueModel || 'MARGIN_FROM_SELL') !== (selected.revenueModel || 'MARGIN_FROM_SELL')) {
        payload.revenueModel = values.revenueModel || 'MARGIN_FROM_SELL'
      }
      const selPct = selected.revenuePercentage != null ? Number(selected.revenuePercentage) : null
      const valPct = values.revenuePercentage != null && values.revenuePercentage !== '' ? Number(values.revenuePercentage) : null
      if (valPct !== selPct) payload.revenuePercentage = valPct

      if (Object.keys(payload).length === 0) {
        message.info('لا تغييرات للحفظ')
      } else {
        await updateShop(selected.id, payload)
        message.success('تم التحديث')
      }
      editImageRef.current?.clearPendingFile?.()
      setEditOpen(false)
      setSelected(null)
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setEditing(false)
    }
  }

  async function onDelete(id) {
    try {
      await deleteShop(id)
      message.success('تم الحذف')
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    }
  }

  const columns = [
    { title: 'رقم', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'الإسم', dataIndex: 'nameAr', key: 'nameAr' },
    { title: 'الهاتف', dataIndex: 'phoneNumber', key: 'phoneNumber' },
    {
      title: 'الصورة', dataIndex: 'imageUrl', key: 'imageUrl',
      render: (v) => v ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Image src={`https://images.leen-go.com/${v}`} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} fallback="https://placehold.co/36x36?text=?" />
          <span style={{ direction: 'ltr', fontSize: 12 }}>{v}</span>
        </span>
      ) : null
    },
    {
      title: 'التصنيف', key: 'mainCategory',
      render: (_, r) => r.mainCategory?.tagAr ? <Tag color="cyan">{r.mainCategory.tagAr}</Tag> : '-'
    },
    {
      title: 'نموذج العمولة',
      key: 'revenueModel',
      width: 160,
      render: (_, r) => (r.revenueModel === 'MARKUP_ON_TOP' ? <Tag color="purple">على السعر</Tag> : <Tag>من سعر البيع</Tag>)
    },
    {
      title: '%',
      dataIndex: 'revenuePercentage',
      key: 'revenuePercentage',
      width: 72,
      render: (v) => (v != null ? String(v) : '—')
    },
    {
      title: 'الحالة', dataIndex: 'isOpened', key: 'isOpened', width: 100,
      render: (v) => v ? <Tag color="green">مفتوح</Tag> : <Tag>مغلق</Tag>
    },
    {
      title: 'مخفي', dataIndex: 'hidden', key: 'hidden', width: 100,
      render: (v) => v ? <Tag color="red">مخفي</Tag> : <Tag color="blue">ظاهر</Tag>
    },
    {
      title: '', key: 'actions', width: 200,
      render: (_, record) => (
        <Space>
          <Button onClick={() => openEdit(record)}>تعديل</Button>
          <Popconfirm title="حذف هذا المتجر؟" onConfirm={() => onDelete(record.id)}>
            <Button danger>حذف</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{ maxWidth: 1100, margin: '40px auto', padding: '0 16px' }}>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>المتاجر</Typography.Title>
        <Button type="primary" onClick={() => setCreateOpen(true)}>متجر جديد</Button>
      </Space>
      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title="إنشاء متجر"
        open={createOpen}
        onCancel={() => {
          createImageRef.current?.clearPendingFile?.()
          setCreateOpen(false)
        }}
        okText="إنشاء"
        confirmLoading={creating}
        onOk={() => createForm.submit()}
      >
        <Form form={createForm} layout="vertical" onFinish={onCreate}>
          <Form.Item label="الإسم" name="nameAr" rules={[{ required: true, message: 'يرجى إدخال الاسم' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="الهاتف" name="phoneNumber">
            <Input />
          </Form.Item>
          <Form.Item label="وقت الافتتاح" name="openTime">
            <Input placeholder="08:00" />
          </Form.Item>
          <Form.Item label="وقت الإغلاق" name="closeTime">
            <Input placeholder="23:00" />
          </Form.Item>
          <Form.Item label="وقت الافتتاح (عطلة)" name="openTimeWeekend">
            <Input placeholder="09:00" />
          </Form.Item>
          <Form.Item label="وقت الإغلاق (عطلة)" name="closeTimeWeekend">
            <Input placeholder="01:00" />
          </Form.Item>
          <Form.Item label="مخفي" name="hidden" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="نموذج العمولة" name="revenueModel" initialValue="MARGIN_FROM_SELL">
            <Select
              options={[
                { value: 'MARGIN_FROM_SELL', label: 'نسبة من سعر البيع للزبون (القديم)' },
                { value: 'MARKUP_ON_TOP', label: 'نسبة فوق سعر المتجر (السعر المخزّن = سعر المتجر)' },
              ]}
            />
          </Form.Item>
          <Form.Item label="نسبة العمولة %" name="revenuePercentage">
            <InputNumber min={0} max={100} step={0.01} style={{ width: '100%' }} placeholder="مثال 10" />
          </Form.Item>
          <Form.Item label="صورة المتجر" name="imageUrl">
            <ImageUploadField ref={createImageRef} placeholder={ADMIN_SHOP_IMAGE_PLACEHOLDER} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="تعديل متجر"
        open={editOpen}
        onCancel={() => {
          editImageRef.current?.clearPendingFile?.()
          setEditOpen(false)
        }}
        okText="حفظ"
        confirmLoading={editing}
        onOk={() => editForm.submit()}
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item label="الإسم" name="nameAr">
            <Input />
          </Form.Item>
          <Form.Item label="الهاتف" name="phoneNumber">
            <Input />
          </Form.Item>
          <Form.Item label="وقت الافتتاح" name="openTime">
            <Input placeholder="08:00" />
          </Form.Item>
          <Form.Item label="وقت الإغلاق" name="closeTime">
            <Input placeholder="23:00" />
          </Form.Item>
          <Form.Item label="وقت الافتتاح (عطلة)" name="openTimeWeekend">
            <Input placeholder="09:00" />
          </Form.Item>
          <Form.Item label="وقت الإغلاق (عطلة)" name="closeTimeWeekend">
            <Input placeholder="01:00" />
          </Form.Item>
          <Form.Item label="مخفي" name="hidden" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="نموذج العمولة" name="revenueModel">
            <Select
              options={[
                { value: 'MARGIN_FROM_SELL', label: 'نسبة من سعر البيع للزبون (القديم)' },
                { value: 'MARKUP_ON_TOP', label: 'نسبة فوق سعر المتجر (السعر المخزّن = سعر المتجر)' },
              ]}
            />
          </Form.Item>
          <Form.Item label="نسبة العمولة %" name="revenuePercentage">
            <InputNumber min={0} max={100} step={0.01} style={{ width: '100%' }} placeholder="مثال 10" />
          </Form.Item>
          <Form.Item label="صورة المتجر" name="imageUrl">
            <ImageUploadField ref={editImageRef} placeholder={ADMIN_SHOP_IMAGE_PLACEHOLDER} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
